import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { axiosInstance } from '../App';
import { ArrowLeft, Printer, Edit, Trash2, Calendar, Package, User, CreditCard, FileText, Check, X, TrendingUp, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { decryptId, encryptId } from '../utils/urlEncryption';
import RevisionTimelineCard from '../components/RevisionTimelineCard';
import PrintFormatSelector from '../components/PrintFormatSelector';
import BookingPrintA4 from '../components/BookingPrintA4';
import BookingPrintThermal from '../components/BookingPrintThermal';
import BookingPrintThermalSummary from '../components/BookingPrintThermalSummary';
import ItemReceiptPrint from '../components/ItemReceiptPrint';
import ItemReceiptSelector from '../components/ItemReceiptSelector';
import { openPrintWindow, getElementOuterHTML } from '../utils/printWindow';
import '../styles/print.css';

const BookingDetail = ({ user, onLogout }) => {
  const { bookingId: encryptedBookingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Decrypt the booking ID from URL
  const bookingId = decryptId(encryptedBookingId);
  
  const [booking, setBooking] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [reminder, setReminder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [expandedPaymentMode, setExpandedPaymentMode] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showItemReceiptSelector, setShowItemReceiptSelector] = useState(false);

  // Check if we're coming from the approval page
  const fromPage = searchParams.get('from'); // 'pending', 'provisional', or 'approval'
  const isAdmin = user?.role === 'Admin' || user?.role === 'Manager';

  useEffect(() => {
    loadBookingDetails();
  }, [bookingId, location.key]);

  const loadBookingDetails = async () => {
    try {
      setLoading(true);
      const [bookingRes, customersRes] = await Promise.all([
        axiosInstance.get(`/bookings/${bookingId}`),
        axiosInstance.get(`/customers`)
      ]);
      
      setBooking(bookingRes.data);
      
      // Find the customer
      const cust = customersRes.data.find(c => c.id === bookingRes.data.customer_id);
      setCustomer(cust);

      // Load reminder for this booking (from reminders collection)
      try {
        const reminderRes = await axiosInstance.get(`/reminders`, {
          params: { order_id: bookingId }
        });
        if (reminderRes.data && reminderRes.data.length > 0) {
          setReminder(reminderRes.data[0]);
        }
      } catch (reminderErr) {
        // Reminder not found is okay
        console.log('No reminder found for booking');
      }
    } catch (err) {
      console.error('Error loading booking:', err);
      toast.error('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setProcessing(true);
      await axiosInstance.post(`/admin/approve/booking/${bookingId}`);
      toast.success('Booking approved successfully');
      navigate('/admin/approvals');
    } catch (err) {
      console.error('Error approving booking:', err);
      toast.error('Failed to approve booking');
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    try {
      setProcessing(true);
      await axiosInstance.post(`/admin/reject/booking/${bookingId}`, {
        reason: rejectionReason
      });
      toast.success('Booking rejected');
      navigate('/admin/approvals');
    } catch (err) {
      console.error('Error rejecting booking:', err);
      toast.error('Failed to reject booking');
      setProcessing(false);
      setShowRejectModal(false);
    }
  };

  const calculateTotals = () => {
    if (!booking) return { subtotal: 0, totalAdvance: 0, balance: 0, cashAdvance: 0, metalAdvanceFineWt: 0 };

    // Calculate subtotal (estimated value)
    let subtotal = 0;
    if (booking.items && booking.items.length > 0 && booking.base24k_rate_ex_gst) {
      const totalWeight = booking.items.reduce((sum, item) => sum + (parseFloat(item.weight) || 0), 0);
      let rate = booking.base24k_rate_ex_gst;
      
      // Apply GST if exclusive
      if (booking.gst_input_style === 'EXCL' && rate > 0) {
        rate = rate * 1.03;
      }
      
      subtotal = totalWeight * rate;
    }

    // Calculate total advance
    const cashAdvance = (booking.advance_payments || []).reduce((sum, payment) => {
      if (payment.mode !== 'Metal Credit') {
        const amount = parseFloat(payment.amount) || parseFloat(payment.inr) || 0;
        // Subtract refunds, add payments
        return payment.transaction_type === 'REFUND' ? sum - amount : sum + amount;
      }
      return sum;
    }, 0);

    const metalAdvanceFineWt = (booking.advance_payments || []).reduce((sum, payment) => {
      if (payment.mode === 'Metal Credit') {
        return sum + (parseFloat(payment.fine_wt) || 0);
      }
      return sum;
    }, 0);

    const totalAdvance = cashAdvance;
    const balance = subtotal - totalAdvance;

    return { subtotal, totalAdvance, balance, cashAdvance, metalAdvanceFineWt };
  };

  const handlePrint = () => {
    setShowPrintModal(true);
  };

  // Extract Old Gold and Metal Credit items from advance payments
  const getOldGoldAndMetalCreditItems = () => {
    const payments = booking?.advance_payments || [];
    
    const items = [];
    
    payments.forEach((payment) => {
      // Old Gold items
      if (payment.mode === 'Old Gold') {
        if (payment.items && Array.isArray(payment.items) && payment.items.length > 0) {
          // Multiple old gold items
          payment.items.forEach((item) => {
            items.push({
              ...item,
              // Map item fields to standardized names
              gross_wt: item.gross_wt || item.grossWt,
              net_wt: item.net_wt || item.netWt,
              less_wt: item.less_wt || item.lessWt,
              fine_wt: item.fine_wt || item.fineWt,
              purity: item.purity,
              rate: item.purchase_rate || item.rate,
              value: item.line_total || item.value,
              description: item.item_name || item.description,
              payment_id: payment.payment_id,
              payment_date: payment.date,
              type: 'old_gold'
            });
          });
        } else {
          // Single old gold payment (top-level fields)
          items.push({
            gross_wt: payment.gross_wt,
            net_wt: payment.net_wt,
            stone_wt: payment.stone_wt,
            less_wt: payment.less_wt,
            purity: payment.purity,
            fine_wt: payment.fine_wt || payment.fine_grams,
            rate: payment.rate_per_gram,
            value: payment.total_value,
            payment_id: payment.payment_id,
            payment_date: payment.date,
            type: 'old_gold'
          });
        }
      }
      
      // Metal Credit items
      if (payment.mode === 'Metal Credit' && payment.deposits && Array.isArray(payment.deposits)) {
        payment.deposits.forEach((deposit) => {
          items.push({
            ...deposit,
            deposits: [deposit], // Wrap single deposit for consistency
            payment_id: payment.payment_id,
            payment_date: payment.date,
            type: 'metal_credit'
          });
        });
      }
    });
    
    return items;
  };

  const handlePrintFormat = (format) => {
    setShowPrintModal(false);
    
    if (format === 'item-receipts') {
      // Show item receipt selector
      setShowItemReceiptSelector(true);
      return;
    }
    
    // Get the HTML content of the selected print format
    let content = '';
    let title = '';
    let pageSize = '';
    
    if (format === 'a4') {
      content = getElementOuterHTML('booking-a4-print-content');
      title = `Booking ${booking?.booking_id} - A4 Print`;
      pageSize = 'A4';
    } else if (format === 'thermal') {
      content = getElementOuterHTML('booking-thermal-print-content');
      title = `Booking ${booking?.booking_id} - Thermal Detail`;
      pageSize = 'thermal';
    } else if (format === 'thermal-summary') {
      content = getElementOuterHTML('booking-thermal-summary-print-content');
      title = `Booking ${booking?.booking_id} - Thermal Summary`;
      pageSize = 'thermal';
    }
    
    // Open print window with the selected content
    if (content) {
      openPrintWindow(content, title, { pageSize });
    } else {
      toast.error('Print content not found. Please try again.');
    }
  };

  const handlePrintSelectedItems = (selectedIndices) => {
    const items = getOldGoldAndMetalCreditItems();
    setShowItemReceiptSelector(false);
    
    if (selectedIndices.length === 0) {
      toast.error('Please select at least one receipt to print');
      return;
    }
    
    // Build HTML for selected receipts
    let receiptsHTML = '';
    selectedIndices.forEach((index) => {
      const receiptElement = document.getElementById(`item-receipt-${index}`);
      if (receiptElement) {
        receiptsHTML += receiptElement.outerHTML;
      }
    });
    
    if (!receiptsHTML) {
      toast.error('Selected receipts not found');
      return;
    }
    
    // Open print window with selected receipts
    openPrintWindow(
      receiptsHTML,
      `Booking ${booking?.booking_id} - Item Receipts`,
      { pageSize: 'thermal' }
    );
  };

  const handleEdit = () => {
    navigate(`/bookings/edit/${encryptId(bookingId)}`);
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/bookings/${bookingId}`);
      toast.success('Booking deleted successfully');
      navigate('/orders/reports');
    } catch (err) {
      toast.error('Failed to delete booking');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const shareOnWhatsApp = () => {
    if (!customer) {
      toast.error('Customer information not available');
      return;
    }
    
    const phone = customer.mobile || customer.phone;
    if (!phone) {
      toast.error('Customer phone number not available');
      return;
    }
    
    // Generate thermal detail format like Order module
    const message = generateThermalDetailText();
    
    const cleanPhone = phone.replace(/\D/g, '').slice(-10);
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/91${cleanPhone}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
  };

  // Generate thermal detail format for WhatsApp
  const generateThermalDetailText = () => {
    const formatDate = (dateStr) => {
      if (!dateStr) return 'N/A';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const formatCurrency = (amount) => {
      const num = parseFloat(amount) || 0;
      return num.toLocaleString('en-IN', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
    };

    // Build message
    let msg = '';
    
    // Header
    msg += '═══════════════════════════════════\n';
    msg += '         CUSTOMER BOOKING\n';
    msg += '═══════════════════════════════════\n';
    msg += `Booking ID: ${booking.booking_id}\n`;
    msg += `Date: ${formatDate(booking.booking_date || booking.created_at)}\n`;
    msg += `Status: ${booking.status}\n\n`;

    // Customer Information
    msg += '───────────────────────────────────\n';
    msg += 'CUSTOMER INFORMATION\n';
    msg += '───────────────────────────────────\n';
    msg += `Name: ${customer.name}\n`;
    if (customer.mobile) msg += `Mobile: ${customer.mobile}\n`;
    if (customer.alternate_mobile) msg += `Alt Mobile: ${customer.alternate_mobile}\n`;
    if (customer.email) msg += `Email: ${customer.email}\n`;
    if (customer.address) msg += `Address: ${customer.address}\n`;
    msg += '\n';

    // Booking Type & Rate Info
    if (booking.booking_type || booking.base_24k_rate) {
      msg += '───────────────────────────────────\n';
      msg += 'BOOKING INFORMATION\n';
      msg += '───────────────────────────────────\n';
      if (booking.booking_type) msg += `Type: ${booking.booking_type}\n`;
      if (booking.base_24k_rate) {
        msg += `24K Rate: ₹{formatCurrency(order.base24k_rate_ex_gst)}/g\n`;
      }
      if (booking.gst_applicable !== false) {
        const gstPercent = booking.gst_percent || 3;
        const gstType = booking.gst_input_style === 'INCL' ? '(Inclusive)' : '(Exclusive)';
        msg += `GST: ${gstPercent}% ${gstType}\n`;
      }
      msg += '\n';
    }

    // Booking Items
    msg += '───────────────────────────────────\n';
    msg += `BOOKING ITEMS (${booking.items?.length || 0})\n`;
    msg += '───────────────────────────────────\n\n';

    booking.items?.forEach((item, idx) => {
      msg += `${idx + 1}. ${item.item_type || 'Item'}\n`;
      msg += '───────────────────────────────────\n';
      
      // Images
      if (item.photos && item.photos.length > 0) {
        item.photos.forEach((photo, photoIdx) => {
          msg += `📷 Image ${photoIdx + 1}: ${photo}\n`;
        });
        msg += '\n';
      }

      // Item details
      const weight = item.weight_type === 'Fixed' 
        ? `${item.weight_fixed}g` 
        : `${item.weight_min}-${item.weight_max}g`;
      const qty = `${item.quantity || 1} piece${parseInt(item.quantity || 1) > 1 ? 's' : ''}`;
      
      msg += `Weight: ${weight}     Qty: ${qty}\n`;
      if (item.purity) msg += `Purity: ${item.purity}\n`;
      if (item.size) msg += `Size: ${item.size}\n`;
      if (item.tag_number) msg += `Tag: ${item.tag_number}\n`;
      if (item.description) msg += `Description: ${item.description}\n`;
      msg += '\n';
    });

    // Payments
    if (booking.advance_payments && booking.advance_payments.length > 0) {
      msg += '───────────────────────────────────\n';
      msg += '💳 PAYMENTS\n';
      msg += '───────────────────────────────────\n\n';

      // Group by mode
      const paymentsByMode = {};
      booking.advance_payments.forEach(payment => {
        const mode = payment.mode || 'Unknown';
        if (!paymentsByMode[mode]) paymentsByMode[mode] = [];
        paymentsByMode[mode].push(payment);
      });

      Object.keys(paymentsByMode).forEach(mode => {
        msg += `${mode.toUpperCase()}\n`;
        msg += '───────────────────────────────────\n';
        
        if (mode === 'Old Gold') {
          // Old Gold - Detailed breakdown
          paymentsByMode[mode].forEach(payment => {
            msg += `Date: ${formatDate(payment.date)}\n`;
            msg += `Amount: ₹${formatCurrency(payment.amount || 0)}\n\n`;

            if (payment.items && payment.items.length > 0) {
              msg += 'Item Details:\n';
              payment.items.forEach((item, itemIdx) => {
                msg += `• Item ${itemIdx + 1}${item.item_name ? `: ${item.item_name}` : ''}\n`;
                if (item.gross_wt) msg += `  Gross Wt: ${item.gross_wt}g\n`;
                if (item.less_wt) msg += `  Less Wt: ${item.less_wt}g\n`;
                if (item.net_wt) msg += `  Net Wt: ${item.net_wt}g\n`;
                if (item.purity) msg += `  Purity: ${item.purity}%\n`;
                if (item.fine_wt) msg += `  Fine Wt: ${item.fine_wt}g\n`;
                if (item.purchase_rate || item.rate) {
                  msg += `  Rate: ₹${formatCurrency(item.purchase_rate || item.rate)}/g\n`;
                }
                if (item.line_total || item.value) {
                  msg += `  Amount: ₹${formatCurrency(item.line_total || item.value)}\n`;
                }
                msg += '\n';
              });
            }
            if (payment.note) msg += `Note: ${payment.note}\n`;
          });
          
          // Old Gold Total
          const totalOldGold = paymentsByMode[mode].reduce((sum, p) => {
            const amount = parseFloat(p.amount) || 0;
            return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
          }, 0);
          msg += `Old Gold Total: ₹${formatCurrency(totalOldGold)}\n\n`;
          
        } else if (mode === 'Metal Credit') {
          // Metal Credit - Detailed breakdown with deposits
          paymentsByMode[mode]
            .filter(p => parseFloat(p.fine_wt || p.fine_grams || 0) > 0 || (p.deposits && p.deposits.length > 0))
            .forEach(payment => {
              const hasDeposits = payment.deposits && Array.isArray(payment.deposits) && payment.deposits.length > 0;
              
              msg += `Date: ${formatDate(payment.date)}\n`;
              
              if (hasDeposits) {
                // Detailed deposit breakdown
                payment.deposits.forEach((deposit, depIdx) => {
                  msg += `\nDeposit ${depIdx + 1}${deposit.item_name ? `: ${deposit.item_name}` : ''}\n`;
                  msg += '───────────────────────────────────\n';
                  
                  if (deposit.type || deposit.type_name_other) {
                    msg += `Type: ${deposit.type_name_other || deposit.type}\n`;
                  }
                  if (deposit.gross_wt) {
                    msg += `Gross Wt: ${deposit.gross_wt}g\n`;
                  }
                  if (deposit.less_wt) {
                    msg += `Less Wt: ${deposit.less_wt}g\n`;
                  }
                  if (deposit.net_wt) {
                    msg += `Net Wt: ${deposit.net_wt}g\n`;
                  }
                  if (deposit.purity || deposit.purity_label) {
                    msg += `Purity: ${deposit.purity_label || deposit.purity + '%'}\n`;
                  }
                  if (deposit.fine_wt) {
                    msg += `Fine Wt: ${deposit.fine_wt}g\n`;
                  }
                  if (deposit.reference) {
                    msg += `Ref: ${deposit.reference}\n`;
                  }
                  
                  // Add photo URLs if available
                  if (deposit.photos && deposit.photos.length > 0) {
                    msg += `Photos (${deposit.photos.length}):\n`;
                    deposit.photos.forEach((photo, photoIdx) => {
                      const photoUrl = typeof photo === 'string' ? photo : (photo.edited || photo.original);
                      if (photoUrl) {
                        msg += `📷 Photo ${photoIdx + 1}: ${photoUrl}\n`;
                      }
                    });
                  }
                  msg += '\n';
                });
              } else {
                // Fallback for old data structure
                if (payment.metal_type) msg += `Type: ${payment.metal_type}\n`;
                if (payment.gross_wt) msg += `Gross Wt: ${payment.gross_wt}g\n`;
                if (payment.net_wt) msg += `Net Wt: ${payment.net_wt}g\n`;
                if (payment.purity) msg += `Purity: ${payment.purity}%\n`;
                msg += `Fine Wt: ${payment.fine_wt || payment.fine_grams || 0}g\n`;
                if (payment.reference) msg += `Ref: ${payment.reference}\n`;
                if (payment.note) msg += `Note: ${payment.note}\n`;
                msg += '\n';
              }
            });
          
          // Calculate and display total fine weight
          const totalFineGold = paymentsByMode[mode].reduce((sum, p) => {
            if (p.deposits && p.deposits.length > 0) {
              return sum + p.deposits.reduce((depSum, dep) => depSum + parseFloat(dep.fine_wt || 0), 0);
            }
            return sum + parseFloat(p.fine_wt || p.fine_grams || 0);
          }, 0);
          
          if (totalFineGold > 0) {
            msg += `Metal Credit Total Fine Gold: ${totalFineGold.toFixed(3)}g\n\n`;
          }
          
        } else {
          // Other payment modes
          paymentsByMode[mode].forEach(payment => {
            const amount = parseFloat(payment.amount) || 0;
            const isRefund = payment.transaction_type === 'REFUND';
            const displayAmount = isRefund ? -amount : amount;
            
            msg += `Date: ${formatDate(payment.date)}\n`;
            msg += `Amount: ${isRefund ? '-' : ''}₹${formatCurrency(Math.abs(displayAmount))}\n`;
            if (isRefund) msg += `[REFUND]\n`;
            
            // Add mode-specific details
            if (payment.mode === 'UPI' && payment.upi_ref) {
              msg += `Ref: ${payment.upi_ref}\n`;
              if (payment.upi_handle) msg += `Handle: ${payment.upi_handle}\n`;
            }
            if (payment.mode === 'Card' && payment.card_last4) {
              msg += `Card: ****${payment.card_last4}\n`;
            }
            if ((payment.mode === 'IMPS' || payment.mode === 'NEFT/RTGS') && payment.utr_ref) {
              msg += `UTR: ${payment.utr_ref}\n`;
            }
            if (payment.mode === 'Cheque' && payment.cheque_no) {
              msg += `Cheque: ${payment.cheque_no}\n`;
            }
            if (payment.note) msg += `Note: ${payment.note}\n`;
            msg += '\n';
          });
          
          // Mode Total
          const modeTotal = paymentsByMode[mode].reduce((sum, p) => {
            const amount = parseFloat(p.amount) || 0;
            return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
          }, 0);
          msg += `${mode} Total: ₹${formatCurrency(modeTotal)}\n\n`;
        }
      });
    }

    // Financial Summary
    msg += '═══════════════════════════════════\n';
    msg += 'FINANCIAL SUMMARY\n';
    msg += '═══════════════════════════════════\n';
    msg += `Estimated Value: ₹${formatCurrency(totals.subtotal)}\n`;
    msg += `Advance Paid: ₹${formatCurrency(totals.totalAdvance)}\n`;
    msg += `Balance Due: ₹${formatCurrency(totals.balance)}\n`;
    msg += '═══════════════════════════════════\n\n';
    
    msg += 'Thank you for choosing us!\n';

    return msg;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-amber-600"></div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Booking not found</p>
          <button onClick={() => navigate('/bookings')} className="btn-primary">
            Back to Bookings
          </button>
        </div>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pb-20">
      <div className="max-w-6xl mx-auto p-2 sm:p-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => fromPage ? navigate('/admin-approvals') : navigate('/bookings')} 
                className="p-2 hover:bg-amber-100 rounded-lg flex-shrink-0"
                title={fromPage ? 'Back to Pending Approvals' : 'Back to Bookings'}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-800">{booking.booking_id}</h1>
                <p className="text-xs sm:text-sm text-gray-600">
                  {new Date(booking.booking_date || booking.created_at).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              {/* Admin Action Buttons - Show when coming from pending/provisional */}
              {fromPage && isAdmin && (
                <div className="flex gap-2">
                  <button
                    onClick={handleApprove}
                    disabled={processing}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold text-sm touch-target disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Check className="w-4 h-4" />
                    <span>Approve {fromPage === 'provisional' ? 'Changes' : ''}</span>
                  </button>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    disabled={processing}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold text-sm touch-target disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <X className="w-4 h-4" />
                    <span>Reject</span>
                  </button>
                </div>
              )}
              
              {/* Regular Action Buttons - Grid Layout for Mobile */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <button
                  onClick={shareOnWhatsApp}
                  className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-green-100 text-green-700 rounded-lg font-semibold hover:bg-green-200 transition-colors text-xs sm:text-sm touch-target"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span>Share</span>
                </button>
                <button
                  onClick={handlePrint}
                  className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-blue-100 text-blue-700 rounded-lg font-semibold hover:bg-blue-200 transition-colors text-xs sm:text-sm touch-target"
                >
                  <Printer className="w-4 h-4" />
                  <span>Print</span>
                </button>
                <button
                  onClick={handleEdit}
                  className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-amber-100 text-amber-700 rounded-lg font-semibold hover:bg-amber-200 transition-colors text-xs sm:text-sm touch-target"
                >
                  <Edit className="w-4 h-4" />
                  <span>Edit</span>
                </button>
                {(user?.role === 'Admin' || user?.role === 'Manager') && (
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-red-100 text-red-700 rounded-lg font-semibold hover:bg-red-200 transition-colors text-xs sm:text-sm touch-target"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Customer & Booking Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
          {/* Customer Info */}
          <div className="bg-white border-2 border-amber-200 rounded-xl p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-3">
              <User className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
              <h2 className="text-base sm:text-lg font-bold text-gray-800">Customer</h2>
            </div>
            {customer && (
              <div className="space-y-2 text-xs sm:text-sm">
                <div className="flex justify-between gap-2">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-semibold text-right">{customer.name}</span>
                </div>
                {customer.phone && (
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600">Phone:</span>
                    <span className="font-semibold">{customer.phone}</span>
                  </div>
                )}
                {customer.mobile && (
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600">Mobile:</span>
                    <span className="font-semibold">{customer.mobile}</span>
                  </div>
                )}
                {customer.alternate_mobile && (
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600">Alternate Mobile:</span>
                    <span className="font-semibold">{customer.alternate_mobile}</span>
                  </div>
                )}
                {customer.email && (
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-semibold text-right break-all">{customer.email}</span>
                  </div>
                )}
                {customer.address && (
                  <div className="flex flex-col gap-1">
                    <span className="text-gray-600">Address:</span>
                    <span className="font-semibold text-xs break-words">{customer.address}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Booking Info */}
          <div className="bg-white border-2 border-amber-200 rounded-xl p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
              <h2 className="text-base sm:text-lg font-bold text-gray-800">Booking Info</h2>
            </div>
            <div className="space-y-2 text-xs sm:text-sm">
              <div className="flex justify-between gap-2">
                <span className="text-gray-600">Date:</span>
                <span className="font-semibold">{new Date(booking.booking_date || booking.created_at).toLocaleDateString('en-IN')}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-gray-600">Created By:</span>
                <span className="font-semibold">{booking.created_by || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Rate Policy Info */}
        {booking.rate_policy && booking.rate_policy !== 'none' && (
          <div className="bg-white border-2 border-blue-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              <h2 className="text-base sm:text-lg font-bold text-gray-800">Rate Policy</h2>
            </div>
            <div className="space-y-2 text-xs sm:text-sm">
              <div className="flex justify-between gap-2">
                <span className="text-gray-600">Policy:</span>
                <span className={`font-bold ${booking.rate_policy === 'lock-today' ? 'text-green-700' : 'text-orange-700'}`}>
                  {booking.rate_policy === 'lock-today' ? '🔒 Rate Locked' : '📅 Rate at Delivery'}
                </span>
              </div>
              {booking.rate_mode && (
                <div className="flex justify-between gap-2">
                  <span className="text-gray-600">Rate Mode:</span>
                  <span className="font-semibold">{booking.rate_mode === 'single' ? 'Single Rate' : 'Per Item'}</span>
                </div>
              )}
              {booking.base24k_rate_ex_gst && (
                <div className="flex justify-between gap-2">
                  <span className="text-gray-600">24K Rate:</span>
                  <span className="font-bold text-green-700">₹{booking.base24k_rate_ex_gst.toLocaleString('en-IN')}/g</span>
                </div>
              )}
              {booking.lock_date && (
                <div className="flex justify-between gap-2">
                  <span className="text-gray-600">Lock Date:</span>
                  <span className="font-semibold">{new Date(booking.lock_date).toLocaleDateString('en-IN')}</span>
                </div>
              )}
              <div className="flex justify-between gap-2 pt-2 border-t border-blue-200">
                <span className="text-gray-600">GST:</span>
                <span className="font-semibold">
                  {booking.gst_applicable !== false ? `${booking.gst_percent || 3}% (${booking.gst_input_style === 'INCL' ? 'Incl.' : 'Excl.'})` : 'Kaccha'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Booking Items */}
        <div className="bg-white border-2 border-amber-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <Package className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
            <h2 className="text-base sm:text-lg font-bold text-gray-800">Items ({booking.items?.length || 0})</h2>
          </div>
          
          <div className="space-y-2 sm:space-y-3">
            {booking.items?.map((item, idx) => {
              // Determine which rate to show for this item
              let itemRate = null;
              let rateLabel = 'Gold Rate';
              
              if (booking.rate_policy === 'lock-today') {
                if (booking.rate_mode === 'per-item') {
                  // Per-item mode - use item.rate if available (populated by backend)
                  if (item.rate) {
                    itemRate = item.rate;
                  } else if (booking.item_rates && Object.keys(booking.item_rates).length > 0) {
                    // Fallback: check item_rates with index-based key
                    const itemKey = `item_${idx}`;
                    itemRate = booking.item_rates[itemKey];
                  } else if (booking.purity_rates && Object.keys(booking.purity_rates).length > 0) {
                    // Last fallback: purity_rates (old format)
                    itemRate = booking.purity_rates[item.booking_type];
                  }
                  rateLabel = 'Gold Rate';
                } else if (booking.base24k_rate_ex_gst) {
                  // Single rate mode - show base rate for all items
                  itemRate = booking.base24k_rate_ex_gst;
                  rateLabel = 'Gold Rate';
                }
              }

              return (
                <div key={idx} className="border-2 border-amber-200 rounded-xl p-3 sm:p-4 bg-amber-50">
                  <div className="flex justify-between items-start mb-2 sm:mb-3">
                    <div>
                      <span className="bg-amber-600 text-white text-xs font-bold px-2 py-1 rounded">#{idx + 1}</span>
                      <span className="ml-2 font-bold text-gray-900 text-sm sm:text-lg">{item.booking_type}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-amber-700 text-lg sm:text-xl">{item.weight}g</div>
                      <div className="text-xs text-gray-600">Weight</div>
                    </div>
                  </div>

                  {/* Show locked gold rate below item - Smaller, cleaner style */}
                  {itemRate ? (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-600">{rateLabel}:</span>
                        <div className="text-right">
                          <span className="font-bold text-green-700">₹{itemRate.toLocaleString('en-IN')}/g</span>
                          <span className="text-gray-500 text-xs ml-1">
                            ({booking.tax_scope === 'KACCHA' ? 'Kaccha' : (booking.gst_input_style === 'EXCL' ? 'Excl. GST' : 'Incl. GST')})
                          </span>
                        </div>
                      </div>
                      {booking.lock_date && (
                        <p className="text-xs text-gray-500 mt-1">
                          Locked: {new Date(booking.lock_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </p>
                      )}
                    </div>
                  ) : booking.rate_policy === 'lock-today' && booking.rate_mode === 'per-item' ? (
                    <div className="mt-2 p-2 bg-orange-50 border-2 border-orange-300 rounded-lg">
                      <p className="text-xs text-orange-800 font-semibold">⚠️ Per-item rate selected but not saved</p>
                      <p className="text-xs text-orange-700 mt-1">Gold rate for this item was not recorded during booking creation.</p>
                    </div>
                  ) : null}

                  {item.public_remark && (
                    <div className="mt-2 p-2 bg-white rounded border border-amber-200">
                      <span className="text-gray-600 text-xs font-semibold">Remark:</span>
                      <p className="text-xs sm:text-sm text-gray-800 mt-1">{item.public_remark}</p>
                    </div>
                  )}

                  {item.internal_remark && user.role !== 'Cashier' && (
                    <div className="mt-2 p-2 bg-yellow-50 rounded border border-yellow-300">
                      <span className="text-yellow-800 text-xs font-semibold">Internal:</span>
                      <p className="text-xs sm:text-sm text-yellow-900 mt-1">{item.internal_remark}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-3 sm:mt-4 pt-3 border-t-2 border-amber-300 bg-amber-50 rounded-lg p-2 sm:p-3">
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="font-bold text-gray-800">Total Weight:</span>
              <span className="font-bold text-amber-700 text-base sm:text-lg">
                {booking.items?.reduce((sum, item) => sum + (parseFloat(item.weight) || 0), 0).toFixed(2)}g
              </span>
            </div>
          </div>
        </div>

        {/* Payments & Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
          {/* Payments - Enhanced with Mode Totals & Collapsible Transactions */}
          <div className="bg-white border-2 border-amber-200 rounded-xl p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
              <h2 className="text-base sm:text-lg font-bold text-gray-800">Payment Details</h2>
            </div>
            
            {booking.advance_payments && booking.advance_payments.length > 0 ? (
              <div className="space-y-3">
                {/* Payment Summary by Mode */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3">
                  <div className="text-xs font-semibold text-gray-700 mb-2">Payment Mode Summary</div>
                  
                  {(() => {
                    // Group payments by mode and calculate totals
                    const modeTotals = {};
                    const paymentsByMode = {};
                    
                    booking.advance_payments.forEach(payment => {
                      const mode = payment.mode || 'Cash';
                      const isRefund = payment.transaction_type === 'REFUND';
                      
                      if (!modeTotals[mode]) {
                        modeTotals[mode] = { cashAmount: 0, fineWeight: 0, count: 0 };
                        paymentsByMode[mode] = [];
                      }
                      
                      const amount = parseFloat(payment.amount) || 0;
                      // Subtract refunds, add payments
                      modeTotals[mode].cashAmount += isRefund ? -amount : amount;
                      modeTotals[mode].fineWeight += parseFloat(payment.fine_wt) || parseFloat(payment.fine_grams) || 0;
                      modeTotals[mode].count += 1;
                      paymentsByMode[mode].push(payment);
                    });
                    
                    // Sort modes: Cash first, then alphabetically
                    const sortedModes = Object.keys(modeTotals).sort((a, b) => {
                      if (a === 'Cash') return -1;
                      if (b === 'Cash') return 1;
                      return a.localeCompare(b);
                    });
                    
                    return (
                      <div className="space-y-1">
                        {sortedModes.map(mode => {
                          const data = modeTotals[mode];
                          const isExpanded = expandedPaymentMode === mode;
                          
                          return (
                            <div key={mode}>
                              {/* Mode Summary Row - Clickable */}
                              <div className="flex justify-between items-center py-1.5 border-b border-green-100 last:border-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-gray-800">{mode}</span>
                                  <button
                                    onClick={() => setExpandedPaymentMode(isExpanded ? null : mode)}
                                    className="text-xs text-blue-600 hover:text-blue-800 font-medium underline cursor-pointer touch-target px-2 py-1 hover:bg-blue-50 rounded transition-colors"
                                  >
                                    ({data.count} transaction{data.count > 1 ? 's' : ''})
                                  </button>
                                </div>
                                <div className="flex flex-col items-end">
                                  {data.cashAmount > 0 && (
                                    <span className="font-bold text-green-700">
                                      ₹{data.cashAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                    </span>
                                  )}
                                  {data.fineWeight > 0 && (
                                    <span className="font-bold text-purple-700">
                                      {data.fineWeight.toFixed(3)}g
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              {/* Expanded Transaction Details for This Mode */}
                              {isExpanded && (
                                <div className="mt-2 mb-3 ml-2 space-y-2 border-l-2 border-blue-300 pl-3">
                                  {paymentsByMode[mode].map((payment, idx) => {
                                    const isRefund = payment.transaction_type === 'REFUND';
                                    return (
                                    <div key={idx} className={`border rounded-lg p-3 shadow-sm ${isRefund ? 'bg-red-50 border-red-300' : 'bg-white border-amber-200'}`}>
                                      {/* Payment Header */}
                                      <div className="flex justify-between items-start pb-2 border-b border-gray-200">
                                        <div className="flex-1">
                                          <span className={`font-semibold text-xs ${isRefund ? 'text-red-700' : 'text-gray-800'}`}>
                                            {isRefund ? '↩️ REFUND' : 'Transaction'} #{idx + 1}
                                          </span>
                                          {payment.date && (
                                            <span className="text-xs text-gray-600 block">
                                              {new Date(payment.date).toLocaleDateString('en-IN', {
                                                day: 'numeric',
                                                month: 'short',
                                                year: 'numeric'
                                              })}
                                            </span>
                                          )}
                                        </div>
                                        <span className={`font-bold text-sm ${isRefund ? 'text-red-700' : 'text-amber-700'}`}>
                                          {payment.amount ? `${isRefund ? '-' : ''}₹${parseFloat(payment.amount).toLocaleString('en-IN')}` : 
                                           payment.fine_wt ? `${parseFloat(payment.fine_wt).toFixed(3)}g` : '-'}
                                        </span>
                                      </div>
                                      
                                      {/* Payment Details */}
                                      <div className="mt-2 space-y-1 text-xs">
                                        {/* UPI Details */}
                                        {payment.mode === 'UPI' && (
                                          <>
                                            {payment.upi_ref && (
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">UPI Reference:</span>
                                                <span className="font-medium text-gray-800">{payment.upi_ref}</span>
                                              </div>
                                            )}
                                            {payment.upi_id && (
                                              <div className="flex justify-between">
                                                <span className="text-gray-600">UPI ID:</span>
                                                <span className="font-medium text-gray-800">{payment.upi_id}</span>
                                              </div>
                                            )}
                                          </>
                                        )}
                                        
                                        {/* Card Details */}
                                        {payment.mode === 'Card' && payment.card_last4 && (
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Card:</span>
                                            <span className="font-medium text-gray-800">**** {payment.card_last4}</span>
                                          </div>
                                        )}
                                        
                                        {/* Bank Transfer Details */}
                                        {(payment.mode === 'IMPS' || payment.mode === 'NEFT/RTGS') && payment.utr_ref && (
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">UTR:</span>
                                            <span className="font-medium text-gray-800">{payment.utr_ref}</span>
                                          </div>
                                        )}
                                        
                                        {/* Cheque Details */}
                                        {payment.mode === 'Cheque' && payment.cheque_no && (
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Cheque No:</span>
                                            <span className="font-medium text-gray-800">{payment.cheque_no}</span>
                                          </div>
                                        )}
                                        
                                        {/* Old Gold Details - Enhanced with full calculations */}
                                        {payment.mode === 'Old Gold' && (
                                          <div className="bg-purple-50 border border-purple-200 rounded p-2 mt-2">
                                            <span className="text-purple-800 font-semibold block mb-2 text-xs">🪙 Old Gold Details:</span>
                                            
                                            {/* Display items if available */}
                                            {payment.items && payment.items.length > 0 ? (
                                              <div className="space-y-2">
                                                {payment.items.map((item, itemIdx) => (
                                                  <div key={itemIdx} className="bg-white rounded p-2 border border-purple-300">
                                                    {item.item_name && (
                                                      <div className="font-medium text-xs text-purple-800 mb-1">
                                                        #{itemIdx + 1}: {item.item_name}
                                                      </div>
                                                    )}
                                                    <div className="grid grid-cols-2 gap-1 text-xs">
                                                      {item.gross_wt && (
                                                        <div className="flex justify-between">
                                                          <span className="text-gray-600">Gross:</span>
                                                          <span className="font-medium">{parseFloat(item.gross_wt).toFixed(3)}g</span>
                                                        </div>
                                                      )}
                                                      {item.less_wt && parseFloat(item.less_wt) > 0 && (
                                                        <div className="flex justify-between">
                                                          <span className="text-gray-600">Less:</span>
                                                          <span className="font-medium text-red-600">{parseFloat(item.less_wt).toFixed(3)}g</span>
                                                        </div>
                                                      )}
                                                      {item.net_wt && (
                                                        <div className="flex justify-between">
                                                          <span className="text-gray-600">Net:</span>
                                                          <span className="font-medium">{parseFloat(item.net_wt).toFixed(3)}g</span>
                                                        </div>
                                                      )}
                                                      {item.purity && (
                                                        <div className="flex justify-between">
                                                          <span className="text-gray-600">Purity:</span>
                                                          <span className="font-medium">{item.purity}%</span>
                                                        </div>
                                                      )}
                                                      {item.fine_wt && (
                                                        <div className="flex justify-between col-span-2 pt-1 border-t border-purple-200 mt-1">
                                                          <span className="text-gray-700 font-semibold">Fine:</span>
                                                          <span className="font-bold text-purple-700">{parseFloat(item.fine_wt).toFixed(3)}g</span>
                                                        </div>
                                                      )}
                                                      {item.purchase_rate && (
                                                        <div className="flex justify-between">
                                                          <span className="text-gray-600">Rate:</span>
                                                          <span className="font-medium">₹{parseFloat(item.purchase_rate).toLocaleString('en-IN')}/g</span>
                                                        </div>
                                                      )}
                                                      {item.line_total && (
                                                        <div className="flex justify-between">
                                                          <span className="text-gray-600">Total:</span>
                                                          <span className="font-bold text-green-700">₹{parseFloat(item.line_total).toLocaleString('en-IN')}</span>
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                ))}
                                                {/* Summary */}
                                                <div className="bg-purple-100 rounded p-2 border border-purple-300 mt-2">
                                                  <div className="flex justify-between text-xs font-bold">
                                                    <span>Total Fine Weight:</span>
                                                    <span className="text-purple-700">{(payment.fine_grams || payment.fine_wt || 0).toFixed(3)}g</span>
                                                  </div>
                                                  <div className="flex justify-between text-xs font-bold mt-1">
                                                    <span>Total Amount:</span>
                                                    <span className="text-green-700">₹{parseFloat(payment.amount).toLocaleString('en-IN')}</span>
                                                  </div>
                                                </div>
                                              </div>
                                            ) : (
                                              /* Fallback for old data without items */
                                              <div className="text-xs text-gray-500 italic">
                                                {payment.fine_grams || payment.fine_wt ? (
                                                  <div>
                                                    <div className="flex justify-between mb-1">
                                                      <span>Fine Weight:</span>
                                                      <span className="font-bold text-purple-700">{(payment.fine_grams || payment.fine_wt).toFixed(3)}g</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                      <span>Amount:</span>
                                                      <span className="font-bold text-green-700">₹{parseFloat(payment.amount).toLocaleString('en-IN')}</span>
                                                    </div>
                                                  </div>
                                                ) : (
                                                  'Details not available for this transaction'
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                        
                                        {/* Metal Credit Details - Enhanced with full calculations */}
                                        {payment.mode === 'Metal Credit' && (
                                          <div className="bg-indigo-50 border border-indigo-200 rounded p-2 mt-2">
                                            <span className="text-indigo-800 font-semibold block mb-2 text-xs">💎 Metal Credit Details:</span>
                                            {payment.fine_wt && (
                                              <div className="flex justify-between text-xs pt-1 border-t border-indigo-300 mt-1 mb-2">
                                                <span className="text-gray-700 font-semibold">Total Fine Weight:</span>
                                                <span className="font-bold text-indigo-700">{parseFloat(payment.fine_wt).toFixed(3)}g</span>
                                              </div>
                                            )}
                                            {payment.deposits && payment.deposits.length > 0 && (
                                              <div className="space-y-2">
                                                <span className="text-gray-700 font-semibold block text-xs">Deposit Breakdown:</span>
                                                {payment.deposits.map((deposit, depIdx) => (
                                                  <div key={depIdx} className="bg-white rounded p-2 border border-indigo-200">
                                                    <div className="font-medium text-xs text-indigo-800 mb-1">
                                                      #{depIdx + 1}: {deposit.type_name_other || deposit.type}
                                                    </div>
                                                    {deposit.gross_wt && (
                                                      <div className="flex justify-between text-xs">
                                                        <span className="text-gray-600">Gross:</span>
                                                        <span className="font-medium">{parseFloat(deposit.gross_wt).toFixed(3)}g</span>
                                                      </div>
                                                    )}
                                                    {deposit.net_wt && (
                                                      <div className="flex justify-between text-xs">
                                                        <span className="text-gray-600">Net:</span>
                                                        <span className="font-medium">{parseFloat(deposit.net_wt).toFixed(3)}g</span>
                                                      </div>
                                                    )}
                                                    {deposit.stone_wt && parseFloat(deposit.stone_wt) > 0 && (
                                                      <div className="flex justify-between text-xs">
                                                        <span className="text-gray-600">Stone:</span>
                                                        <span className="font-medium text-red-600">{parseFloat(deposit.stone_wt).toFixed(3)}g</span>
                                                      </div>
                                                    )}
                                                    {deposit.purity && (
                                                      <div className="flex justify-between text-xs">
                                                        <span className="text-gray-600">Purity:</span>
                                                        <span className="font-medium">{deposit.purity}</span>
                                                      </div>
                                                    )}
                                                    {deposit.fine_wt && (
                                                      <div className="flex justify-between text-xs pt-1 border-t border-indigo-200 mt-1">
                                                        <span className="text-gray-700 font-semibold">Fine Wt:</span>
                                                        <span className="font-bold text-indigo-700">{parseFloat(deposit.fine_wt).toFixed(3)}g</span>
                                                      </div>
                                                    )}
                                                    {/* Show deposit photos if available */}
                                                    {deposit.photos && deposit.photos.length > 0 && (
                                                      <div className="flex gap-2 flex-wrap mt-2 pt-2 border-t border-indigo-200">
                                                        {deposit.photos.map((photo, photoIdx) => {
                                                          const displayImage = typeof photo === 'string' ? photo : photo.edited || photo.original;
                                                          return (
                                                            <img
                                                              key={photoIdx}
                                                              src={displayImage}
                                                              alt={`Deposit ${depIdx + 1} - Photo ${photoIdx + 1}`}
                                                              className="w-12 h-12 object-cover rounded border border-indigo-300 cursor-pointer hover:border-indigo-500 transition-all hover:scale-105"
                                                              onClick={() => setPreviewImage(displayImage)}
                                                            />
                                                          );
                                                        })}
                                                      </div>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                        
                                        {/* Remarks/Notes for all payment types */}
                                        {(payment.note || payment.remark || payment.remarks) && (
                                          <div className="mt-2 pt-2 border-t border-gray-200 bg-yellow-50 rounded p-2">
                                            <span className="text-gray-600 font-semibold text-xs">📝 Notes/Remarks:</span>
                                            <p className="text-gray-800 text-xs mt-1">{payment.note || payment.remark || payment.remarks}</p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        
                        {/* Grand Total */}
                        <div className="flex justify-between items-center pt-2 mt-2 border-t-2 border-green-300">
                          <span className="font-bold text-gray-900">Net Advance</span>
                          <div className="flex flex-col items-end">
                            {(() => {
                              const grandTotal = booking.advance_payments.reduce((sum, p) => {
                                const amount = parseFloat(p.amount) || 0;
                                // Subtract refunds, add payments
                                return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
                              }, 0);
                              // Fixed: Only count fine_wt (don't add fine_grams as it's the same value)
                              const totalFineWeight = booking.advance_payments.reduce((sum, p) => 
                                sum + (parseFloat(p.fine_wt) || 0), 0);
                              
                              return (
                                <>
                                  {grandTotal !== 0 && (
                                    <span className="font-bold text-green-700 text-base">
                                      ₹{grandTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                    </span>
                                  )}
                                  {totalFineWeight > 0 && (
                                    <span className="font-bold text-purple-700">
                                      {totalFineWeight.toFixed(3)}g
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-500">No payments recorded</p>
            )}
          </div>

          {/* Summary */}
          <div className="bg-gradient-to-br from-amber-100 to-orange-100 border-2 border-amber-300 rounded-xl p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
              <h2 className="text-base sm:text-lg font-bold text-gray-800">Summary</h2>
            </div>
            <div className="space-y-2 text-xs sm:text-sm">
              {totals.subtotal > 0 && (
                <div className="flex justify-between gap-2">
                  <span className="text-gray-700">Est. Value:</span>
                  <span className="font-bold text-gray-800">{formatCurrency(totals.subtotal)}</span>
                </div>
              )}
              <div className="flex flex-col gap-1">
                <div className="flex justify-between gap-2">
                  <span className="text-gray-700">Advance:</span>
                  <span className="font-bold text-green-700">{formatCurrency(totals.totalAdvance)}</span>
                </div>
                {totals.cashAdvance > 0 && (
                  <div className="flex justify-between pl-4 text-xs">
                    <span className="text-gray-600">Cash:</span>
                    <span className="font-semibold text-gray-700">{formatCurrency(totals.cashAdvance)}</span>
                  </div>
                )}
                {totals.metalAdvanceFineWt > 0 && (
                  <div className="flex justify-between pl-4 text-xs">
                    <span className="text-gray-600">Metal:</span>
                    <span className="font-semibold text-gray-700">
                      {totals.metalAdvanceFineWt.toFixed(2)}g
                    </span>
                  </div>
                )}
              </div>
              {totals.subtotal > 0 && (
                <div className="flex justify-between pt-2 border-t-2 border-amber-400 gap-2">
                  <span className="font-bold text-gray-800">Balance:</span>
                  <span className="font-bold text-base sm:text-xl text-red-700">{formatCurrency(totals.balance)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment Reminder - Show from booking.reminder_date OR full reminder object */}
        {(reminder || booking.reminder_date) && (
          <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-bold text-blue-800 mb-3 flex items-center gap-2">
              <span>💰</span> Payment Reminder Set
            </h2>
            {reminder ? (
              /* Full reminder object exists */
              <div className="space-y-2 text-xs sm:text-sm">
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-gray-700 font-semibold">Status:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-bold w-fit ${
                    reminder.status === 'resolved' ? 'bg-green-500 text-white' :
                    reminder.status === 'snoozed' ? 'bg-orange-500 text-white' :
                    reminder.status === 'overdue' ? 'bg-red-500 text-white' :
                    'bg-blue-500 text-white'
                  }`}>{reminder.status.toUpperCase()}</span>
                </div>
                {reminder.reason_tags && reminder.reason_tags.length > 0 && (
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                    <span className="text-gray-700 font-semibold">Reminder Reason:</span>
                    <div className="flex flex-wrap gap-1">
                      {reminder.reason_tags.map((tag, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-blue-200 text-blue-800 rounded-full text-xs font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-gray-700 font-semibold">Due Date & Time:</span>
                  <span className="font-bold text-blue-800">
                    {new Date(reminder.due_at).toLocaleString('en-IN', { 
                      day: 'numeric', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit', hour12: true 
                    })}
                  </span>
                </div>
                
                {/* Target Collection (Amount to Collect) */}
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2 bg-red-50 p-2 rounded-lg border border-red-200">
                  <span className="text-gray-700 font-semibold">🎯 Target Collection:</span>
                  <span className="font-bold text-red-700 text-base">₹{parseFloat(reminder.amount_due).toLocaleString('en-IN')}</span>
                </div>
                
                {reminder.channels && reminder.channels.length > 0 && (
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                    <span className="text-gray-700 font-semibold">Channel(s):</span>
                    <div className="flex flex-wrap gap-1">
                      {reminder.channels.map((channel, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium border border-green-300">
                          {channel === 'whatsapp' ? '📱 WhatsApp' :
                           channel === 'sms' ? '💬 SMS' :
                           channel === 'email' ? '✉️ Email' :
                           channel === 'call' ? '📞 Call' :
                           channel === 'push' ? '🔔 App Push' : channel}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {reminder.notes && (
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <span className="text-gray-700 font-semibold">Notes:</span>
                    <p className="text-gray-800 mt-1 break-words">{reminder.notes}</p>
                  </div>
                )}
                {reminder.message_history && reminder.message_history.length > 0 && (
                  <div className="mt-3 pt-3 border-t-2 border-blue-300">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare className="w-4 h-4 text-blue-700" />
                      <span className="text-gray-700 font-bold">Follow-up History ({reminder.message_history.length})</span>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {reminder.message_history.map((msg, idx) => (
                        <div key={idx} className="bg-white border border-blue-200 rounded-lg p-2">
                          <div className="flex justify-between items-start mb-1">
                            <span className="text-xs font-semibold text-blue-700">{msg.channel.toUpperCase()}</span>
                            <span className="text-xs text-gray-500">
                              {new Date(msg.sent_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          {msg.message_text && <p className="text-xs text-gray-700 break-words">{msg.message_text}</p>}
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              msg.status === 'sent' ? 'bg-green-100 text-green-700' :
                              msg.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                            }`}>{msg.status}</span>
                            {msg.sent_by && <span className="text-xs text-gray-500">by {msg.sent_by}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Simple reminder - just date from booking object */
              <div className="space-y-2 text-xs sm:text-sm">
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-gray-700 font-semibold">Reminder Date:</span>
                  <span className="font-bold text-blue-800">
                    {new Date(booking.reminder_date).toLocaleDateString('en-IN', { 
                      day: 'numeric', month: 'short', year: 'numeric'
                    })}
                  </span>
                </div>
                <p className="text-xs text-gray-500 italic">
                  💡 Full reminder details will appear here once payment reminder is created via the Reminders feature.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Signatures Section */}
        {(booking.management_signature || booking.customer_signature) && (
          <div className="bg-white border-2 border-purple-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-3">✍️ Signatures</h2>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {booking.management_signature && (
                <div className="bg-purple-50 rounded-xl p-3 border border-purple-200">
                  <p className="text-xs text-gray-600 mb-2 font-semibold">Management Signature</p>
                  <img 
                    src={booking.management_signature} 
                    alt="Management Signature" 
                    className="w-full h-20 sm:h-24 object-contain bg-white rounded-lg border"
                  />
                  {booking.management_signature_name && (
                    <p className="text-center text-sm font-bold text-purple-700 mt-2">
                      {booking.management_signature_name}
                    </p>
                  )}
                </div>
              )}
              {booking.customer_signature && (
                <div className="bg-purple-50 rounded-xl p-3 border border-purple-200">
                  <p className="text-xs text-gray-600 mb-2 font-semibold">Customer Signature</p>
                  <img 
                    src={booking.customer_signature} 
                    alt="Customer Signature" 
                    className="w-full h-20 sm:h-24 object-contain bg-white rounded-lg border"
                  />
                  {customer && (
                    <p className="text-center text-sm font-bold text-purple-700 mt-2">
                      {customer.name}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Revision History Section */}
        {booking.revision_history && booking.revision_history.length > 0 && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4 sm:p-6 border-2 border-blue-200 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-bold text-gray-800">Revision History</h2>
              <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded-full">
                {booking.revision_history.length} revision{booking.revision_history.length > 1 ? 's' : ''}
              </span>
            </div>
            <div className="space-y-3">
              {booking.revision_history.map((revision, idx) => (
                <RevisionTimelineCard 
                  key={idx} 
                  revision={revision} 
                  revisionNumber={booking.revision_history.length - idx}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Reject Confirmation Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 sm:p-6 max-w-md w-full">
            <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">Reject Booking?</h2>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for rejecting this booking:
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="w-full border-2 border-gray-300 rounded-lg p-2 sm:p-3 text-sm mb-4 min-h-[100px]"
              placeholder="Enter reason for rejection..."
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
                disabled={processing}
                className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={processing}
                className="flex-1 px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 text-sm disabled:opacity-50"
              >
                {processing ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-4 sm:p-6 max-w-md w-full">
            <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">Delete Booking?</h2>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this booking? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="max-w-4xl max-h-screen">
            <img 
              src={previewImage} 
              alt="Preview" 
              className="max-w-full max-h-screen object-contain"
            />
          </div>
        </div>
      )}

      {/* Print Format Selector */}
      <PrintFormatSelector 
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        onSelectFormat={handlePrintFormat}
        hasItemReceipts={getOldGoldAndMetalCreditItems().length > 0}
      />

      {/* Print Format Components (Hidden from screen, only for printing) */}
      <div className="print-formats-container">
        {/* Print Components */}
        <BookingPrintA4 booking={booking} customer={customer} />
        <BookingPrintThermal booking={booking} customer={customer} />
        <BookingPrintThermalSummary booking={booking} customer={customer} />
      </div>

      {/* Item Receipt Selector Modal */}
      <ItemReceiptSelector
        isOpen={showItemReceiptSelector}
        onClose={() => setShowItemReceiptSelector(false)}
        items={getOldGoldAndMetalCreditItems()}
        onPrintSelected={handlePrintSelectedItems}
      />

      {/* Item Receipt Print Components (Hidden from screen, only for printing) */}
      <div className="item-receipts-container">
        {getOldGoldAndMetalCreditItems().map((item, index) => (
          <ItemReceiptPrint
            key={index}
            item={item}
            itemIndex={index}
            customer={customer}
            moduleRef={`Booking: ${booking?.booking_id}`}
            transactionDate={item.payment_date || booking?.created_at}
            transactionId={item.payment_id}
          />
        ))}
      </div>
    </div>
  );
};

export default BookingDetail;
